import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import db from '../data/db';
import { getCardCounts } from '../data/fsrs';
import EmptyState from './EmptyState';
import { FiPlus, FiPlay, FiMoreVertical, FiTrash2, FiEdit2, FiBookOpen, FiLock, FiZap } from 'react-icons/fi';

export default function DeckBrowser() {
  const [menuOpen, setMenuOpen] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const navigate = useNavigate();

  // Live-query decks + cards so the list reactively reflects DB writes
  // (GCSE Library "Add to my decks", inline create, sync pulls). Replaces a
  // manual loadDecks() that only ran on mount and missed updates.
  const decks = useLiveQuery(() => db.decks.toArray(), [], []);
  const allCards = useLiveQuery(() => db.cards.toArray(), [], []);

  const deckCounts = {};
  if (decks && allCards) {
    for (const deck of decks) {
      const cards = allCards.filter(c => c.deckId === deck.id);
      deckCounts[deck.id] = getCardCounts(cards);
    }
  }

  async function deleteDeck(deckId, e) {
    e.stopPropagation();
    await db.cards.where('deckId').equals(deckId).delete();
    await db.decks.delete(deckId);
    setMenuOpen(null);
    setConfirmDeleteId(null);
  }

  if (decks === undefined) {
    return <div className="page"><div className="loading-spinner" /></div>;
  }

  if (decks.length === 0) {
    return (
      <div className="page">
        <EmptyState
          icon="📚"
          title="No decks yet"
          description="Paste your notes and let AI build your first deck, or browse the GCSE Library for ready-made subject packs. You can also create decks by hand with New Deck."
          actionLabel="✨ Generate with AI"
          onAction={() => navigate('/decks/generate')}
          secondaryActionLabel="Browse GCSE Library"
          onSecondaryAction={() => navigate('/library')}
        />
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Your Decks</h1>
          <p className="page-subtitle">
            {decks.length} deck{decks.length !== 1 ? 's' : ''} &middot;{' '}
            {Object.values(deckCounts).reduce((sum, c) => sum + c.total, 0)} cards total
          </p>
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-sm)', flexWrap: 'wrap' }}>
          <button className="btn btn-secondary" onClick={() => navigate('/library')}>
            <FiBookOpen /> GCSE Library
          </button>
          <button className="btn btn-secondary" onClick={() => navigate('/decks/new')}>
            <FiPlus /> New Deck
          </button>
          <button className="btn btn-primary" onClick={() => navigate('/decks/generate')}>
            <FiZap /> Generate with AI
          </button>
        </div>
      </div>

      <div className="deck-grid">
        {decks.map(deck => {
          const counts = deckCounts[deck.id] || { newCount: 0, learningCount: 0, dueCount: 0, total: 0 };
          const hasDue = counts.newCount + counts.learningCount + counts.dueCount > 0;

          return (
            <div
              key={deck.id}
              className="deck-card"
              onClick={() => navigate(`/deck/${deck.id}`)}
            >
              <div className="deck-card-header">
                <span className="deck-emoji">{deck.emoji || '📖'}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)' }}>
                  {deck.isSystem ? (
                    <span
                      title="Library deck (cannot be deleted)"
                      style={{
                        color: 'var(--text-tertiary)',
                        fontSize: '0.7rem',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '3px',
                        padding: '2px 6px',
                        border: '1px solid var(--border-secondary)',
                        borderRadius: 'var(--radius-sm)',
                      }}
                    >
                      <FiLock size={10} /> Library
                    </span>
                  ) : null}
                  <div className="deck-menu-wrapper">
                    <button
                      className="deck-menu-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        const nextOpen = menuOpen === deck.id ? null : deck.id;
                        setMenuOpen(nextOpen);
                        if (nextOpen !== deck.id) setConfirmDeleteId(null);
                      }}
                      aria-label="Deck options"
                    >
                      <FiMoreVertical />
                    </button>
                    {menuOpen === deck.id && (
                      <div className="deck-menu">
                        <button onClick={(e) => {
                          e.stopPropagation();
                          setMenuOpen(null);
                          setConfirmDeleteId(null);
                          navigate(`/deck/${deck.id}/edit`);
                        }}>
                          <FiEdit2 /> Edit
                        </button>
                        {!deck.isSystem && (
                          confirmDeleteId === deck.id ? (
                            <>
                              <button
                                className="danger"
                                onClick={(e) => deleteDeck(deck.id, e)}
                                aria-label="Confirm delete deck"
                                style={{ fontWeight: 600 }}
                              >
                                <FiTrash2 /> Confirm delete
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setConfirmDeleteId(null);
                                }}
                                aria-label="Cancel delete"
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <button
                              className="danger"
                              onClick={(e) => {
                                e.stopPropagation();
                                setConfirmDeleteId(deck.id);
                              }}
                              aria-label="Delete deck"
                            >
                              <FiTrash2 /> Delete
                            </button>
                          )
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <h3 className="deck-name">{deck.name}</h3>
              {deck.description && <p className="deck-description">{deck.description}</p>}

              <div className="deck-counts">
                <span className="count-new" title="New cards">{counts.newCount} new</span>
                <span className="count-learning" title="Learning">{counts.learningCount} learning</span>
                <span className="count-due" title="Due for review">{counts.dueCount} due</span>
              </div>

              <div className="deck-progress">
                <div className="progress-bar">
                  {counts.total > 0 && (
                    <>
                      <div
                        className="progress-segment mature"
                        style={{ width: `${((counts.total - counts.newCount - counts.learningCount - counts.dueCount) / counts.total) * 100}%` }}
                      />
                      <div
                        className="progress-segment due"
                        style={{ width: `${(counts.dueCount / counts.total) * 100}%` }}
                      />
                      <div
                        className="progress-segment learning"
                        style={{ width: `${(counts.learningCount / counts.total) * 100}%` }}
                      />
                      <div
                        className="progress-segment new"
                        style={{ width: `${(counts.newCount / counts.total) * 100}%` }}
                      />
                    </>
                  )}
                </div>
              </div>

              {hasDue && (
                <button
                  className="btn btn-study"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/study/${deck.id}`);
                  }}
                >
                  <FiPlay /> Study Now
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
